import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
    $: any;
    jQuery: any;
  }
}

interface GalleryImage {
  id: number;
  title: string;
  category: string;
  image: string;
  original_image?: string;
  description?: string;
  active: boolean;
  order_position: number;
  created_at: string;
}

const Gallery: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }

    // Fetch gallery images
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/gallery?active=true');
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    } finally {
      setLoading(false);
    }
  };

  const openImagePopup = (image: GalleryImage) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      {/* Page Banner */}
      <section 
        className="banner__inner-page bg-image pt-160 pb-160" 
        data-background="/assets/images/banner/banner-inner-page.jpg"
      >
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Our Gallery</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Gallery</span>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="project-area pt-120 pb-120">
        <div className="container-fluid px-4">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>Loading gallery...</p>
            </div>
          ) : images.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>No images found in gallery.</p>
            </div>
          ) : (
            <div className="row g-4">
              {images.map((image, index) => {
                const delay = index % 3 === 0 ? '00ms' : index % 3 === 1 ? '200ms' : '400ms';

                return (
                  <div 
                    key={image.id} 
                    className="col-lg-4 col-md-6 wow fadeInDown" 
                    data-wow-delay={delay} 
                    data-wow-duration="1500ms"
                  >
                    <div 
                      className="project__item" 
                      onClick={() => openImagePopup(image)}
                      style={{ cursor: 'pointer', height: '350px' }}
                    >
                      <div className="image" style={{ height: '100%', overflow: 'hidden', borderRadius: '8px' }}>
                        <img 
                          src={`http://localhost:5000${image.image}`} 
                          alt={image.title}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/assets/images/project/project-image1.jpg';
                          }}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            objectPosition: 'center',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        />
                      </div>
                      <div className="project__content">
                        <div className="con">
                          <h4 className="text-white">{image.title}</h4>
                          <span className="text-white">{image.category}</span>
                        </div>
                        <div className="next-btn">
                          <i className="fa-regular fa-expand"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={closeImagePopup}
        >
          <button
            onClick={closeImagePopup}
            style={{
              position: 'absolute',
              top: '20px',
              right: '30px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '40px',
              cursor: 'pointer',
              zIndex: 10000,
              transition: 'transform 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(90deg)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(0deg)'}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>

          <div 
            style={{
              maxWidth: '95%',
              width: 'auto',
              position: 'relative',
              animation: 'zoomIn 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '95vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              flex: '1', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              overflow: 'hidden',
              borderRadius: '10px 10px 0 0',
              backgroundColor: '#000',
              maxHeight: 'calc(95vh - 150px)'
            }}>
              <img 
                src={`http://localhost:5000${selectedImage.original_image || selectedImage.image}`}
                alt={selectedImage.title}
                style={{
                  width: 'auto',
                  height: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
            <div style={{
              backgroundColor: 'white',
              padding: '25px 30px',
              borderRadius: '0 0 10px 10px',
              textAlign: 'center',
              boxShadow: '0 -5px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ color: '#0C2239', marginBottom: '12px', fontSize: '26px', fontWeight: '700', letterSpacing: '-0.02em' }}>
                {selectedImage.title}
              </h3>
              <p style={{ color: '#03A84A', marginBottom: '12px', fontSize: '16px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {selectedImage.category}
              </p>
              {selectedImage.description && (
                <p style={{ color: '#666', fontSize: '15px', lineHeight: '1.7', marginBottom: '0', maxWidth: '800px', margin: '0 auto' }}>
                  {selectedImage.description}
                </p>
              )}
            </div>
          </div>

          <style>
            {`
              @keyframes zoomIn {
                from {
                  opacity: 0;
                  transform: scale(0.3);
                }
                to {
                  opacity: 1;
                  transform: scale(1);
                }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default Gallery;
